import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  X,
  CheckCircle2,
  Clock,
  Download,
  Mail,
  User,
  Users,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { ReferralDetail } from "@/hooks/useReferralFunnel";
import { getReferralTypeConfig } from "@/utils/referralHelpers";

interface ReferralDetailGroupedProps {
  data: ReferralDetail[];
  selectedLaunchId?: number;
  onClearLaunchFilter: () => void;
}

interface InviterGroup {
  inviterSubmissionId: number;
  inviterName: string;
  inviterEmail: string;
  inviterCode: string | null;
  launchName: string;
  referrals: ReferralDetail[];
  respondedCount: number;
  pendingCount: number;
}

const ITEMS_PER_PAGE = 10;

export function ReferralDetailGrouped({
  data,
  selectedLaunchId,
  onClearLaunchFilter,
}: ReferralDetailGroupedProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "responded" | "pending">("all");
  const [currentPage, setCurrentPage] = useState(1);

  // Agrupar datos por invitador
  const groupedData = useMemo(() => {
    // Filtrar por lanzamiento si está seleccionado
    let filtered = data;
    if (selectedLaunchId) {
      filtered = filtered.filter((item) => item.launchId === selectedLaunchId);
    }

    // Agrupar por invitador
    const groups = new Map<number, InviterGroup>();

    filtered.forEach((item) => {
      if (!groups.has(item.inviterSubmissionId)) {
        groups.set(item.inviterSubmissionId, {
          inviterSubmissionId: item.inviterSubmissionId,
          inviterName: item.inviterName,
          inviterEmail: item.inviterEmail,
          inviterCode: item.inviterCode,
          launchName: item.launchName,
          referrals: [],
          respondedCount: 0,
          pendingCount: 0,
        });
      }

      const group = groups.get(item.inviterSubmissionId)!;
      group.referrals.push(item);
      if (item.status === "responded") {
        group.respondedCount++;
      } else {
        group.pendingCount++;
      }
    });

    return Array.from(groups.values());
  }, [data, selectedLaunchId]);

  // Filtrar grupos
  const filteredGroups = useMemo(() => {
    let result = groupedData;

    // Filtrar por estado (solo mostrar grupos que tienen referidos con ese estado)
    if (statusFilter !== "all") {
      result = result
        .map((group) => ({
          ...group,
          referrals: group.referrals.filter((r) => r.status === statusFilter),
          respondedCount:
            statusFilter === "responded"
              ? group.referrals.filter((r) => r.status === "responded").length
              : 0,
          pendingCount:
            statusFilter === "pending"
              ? group.referrals.filter((r) => r.status === "pending").length
              : 0,
        }))
        .filter((group) => group.referrals.length > 0);
    }

    // Filtrar por búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (group) =>
          group.inviterEmail.toLowerCase().includes(query) ||
          group.inviterName.toLowerCase().includes(query) ||
          (group.inviterCode && group.inviterCode.toLowerCase().includes(query)) ||
          group.referrals.some((r) => r.referralEmail.toLowerCase().includes(query))
      );
    }

    // Ordenar por cantidad de referidos (desc)
    result.sort((a, b) => b.referrals.length - a.referrals.length);

    return result;
  }, [groupedData, statusFilter, searchQuery]);

  // Paginación
  const totalPages = Math.ceil(filteredGroups.length / ITEMS_PER_PAGE);
  const paginatedGroups = filteredGroups.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Resetear página cuando cambian filtros
  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  // Calcular totales
  const totalReferrals = data.length;
  const respondedCount = data.filter((d) => d.status === "responded").length;
  const pendingCount = data.filter((d) => d.status === "pending").length;

  // Exportar a CSV
  const handleExport = () => {
    const headers = [
      "Invitador",
      "Email Invitador",
      "Código",
      "Email Referido",
      "Estado",
      "Tipo Relación",
      "Fecha Respuesta",
      "Lanzamiento",
    ];

    const rows = data
      .filter((item) => !selectedLaunchId || item.launchId === selectedLaunchId)
      .map((item) => [
        item.inviterName,
        item.inviterEmail,
        item.inviterCode || "",
        item.referralEmail,
        item.status === "responded" ? "Respondido" : "Pendiente",
        item.referralType || "",
        item.respondedAt ? new Date(item.respondedAt).toLocaleDateString() : "",
        item.launchName,
      ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `referidos_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Detalle por Persona</CardTitle>
            <CardDescription>
              Referidos agrupados por invitador - {filteredGroups.length} invitadores
              {selectedLaunchId && (
                <Button
                  variant="link"
                  size="sm"
                  className="ml-2 p-0 h-auto text-primary"
                  onClick={onClearLaunchFilter}
                >
                  <X className="mr-1 h-3 w-3" />
                  Quitar filtro de lanzamiento
                </Button>
              )}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Exportar CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filtros */}
        <div className="mb-4 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por email, nombre o código..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleFilterChange();
                }}
                className="pl-9"
              />
            </div>
          </div>
          <Select
            value={statusFilter}
            onValueChange={(value: "all" | "responded" | "pending") => {
              setStatusFilter(value);
              handleFilterChange();
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos ({totalReferrals})</SelectItem>
              <SelectItem value="responded">Respondidos ({respondedCount})</SelectItem>
              <SelectItem value="pending">Pendientes ({pendingCount})</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Lista agrupada */}
        {filteredGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Mail className="h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-sm text-muted-foreground">
              No se encontraron referidos con los filtros actuales
            </p>
          </div>
        ) : (
          <>
            <Accordion type="multiple" className="space-y-2">
              {paginatedGroups.map((group) => (
                <AccordionItem
                  key={group.inviterSubmissionId}
                  value={group.inviterSubmissionId.toString()}
                  className="border rounded-lg px-4"
                >
                  <AccordionTrigger className="hover:no-underline py-3">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">{group.inviterName}</p>
                          <p className="text-xs text-muted-foreground">
                            {group.inviterEmail}
                            {group.inviterCode && (
                              <span className="ml-2 text-muted-foreground/70">
                                • Código: {group.inviterCode}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="gap-1">
                          <Users className="h-3 w-3" />
                          {group.referrals.length}
                        </Badge>
                        {group.respondedCount > 0 && (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            {group.respondedCount}
                          </Badge>
                        )}
                        {group.pendingCount > 0 && (
                          <Badge
                            variant="outline"
                            className="text-amber-600 border-amber-300 gap-1"
                          >
                            <Clock className="h-3 w-3" />
                            {group.pendingCount}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pt-2 pb-4 space-y-2">
                      {group.referrals.map((referral, idx) => {
                        const typeConfig = getReferralTypeConfig(referral.referralType);
                        return (
                          <div
                            key={`${referral.referralEmail}-${idx}`}
                            className="flex items-center justify-between p-3 rounded-md bg-muted/50 hover:bg-muted transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-background border">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div>
                                <p className="font-mono text-sm">{referral.referralEmail}</p>
                                {referral.referralType && (
                                  <Badge
                                    className={`mt-1 ${typeConfig.bgColor} ${typeConfig.color} hover:${typeConfig.bgColor} text-xs`}
                                  >
                                    {typeConfig.icon} {typeConfig.label}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {referral.respondedAt && (
                                <span className="text-xs text-muted-foreground">
                                  {new Date(referral.respondedAt).toLocaleDateString("es-ES", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </span>
                              )}
                              {referral.status === "responded" ? (
                                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                                  <CheckCircle2 className="mr-1 h-3 w-3" />
                                  Respondido
                                </Badge>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="text-amber-600 border-amber-300"
                                >
                                  <Clock className="mr-1 h-3 w-3" />
                                  Pendiente
                                </Badge>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1} a{" "}
                  {Math.min(currentPage * ITEMS_PER_PAGE, filteredGroups.length)} de{" "}
                  {filteredGroups.length} invitadores
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Página {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
